import { useSyncExternalStore } from 'react'

/**
 * Claro u oscuro: Aurora o Arena.
 *
 * Manda el sistema hasta que el jugador toca el botón de la barra; desde ahí
 * manda lo que eligió, guardado en este navegador. No hay una tercera opción
 * «como el sistema» a propósito: son dos pantallas que se reconocen de un
 * vistazo, y un botón de dos estados se entiende sin leer nada. El que quiera
 * volver a seguir al sistema borra los datos del sitio, que es un caso raro.
 *
 * El tema se escribe como `data-tema` en `<html>` y los tokens hacen el resto.
 * `index.html` hace lo mismo antes de que cargue React, con la misma clave, para
 * que el primer cuadro ya salga del color correcto.
 *
 * Es un solo estado para toda la página y no uno por componente: el botón vive
 * en dos barras, y dos copias del estado terminarían pisándose (una elige claro,
 * la otra sigue al sistema y lo vuelve a poner oscuro).
 */

export type Tema = 'claro' | 'oscuro'

/** La misma que lee el guion de arranque de `index.html`. */
const CLAVE = 'domino.tema'

/** El color de la barra del navegador en el teléfono: el `--fondo` de cada tema. */
const COLOR_BARRA: Record<Tema, string> = {
  oscuro: '#080b14',
  claro: '#f6f4ff',
}

/* El almacenamiento puede no estar (ventana privada, sitio bloqueado): el tema
   entonces vive solo lo que dura la pestaña, que es lo mejor que se puede. */
function guardado(): Tema | null {
  try {
    const valor = localStorage.getItem(CLAVE)
    return valor === 'claro' || valor === 'oscuro' ? valor : null
  } catch {
    return null
  }
}

function guardar(tema: Tema): void {
  try {
    localStorage.setItem(CLAVE, tema)
  } catch {
    // Sin dónde guardarlo: vale para esta visita y nada más.
  }
}

const consulta = window.matchMedia('(prefers-color-scheme: light)')

let elegido: Tema | null = guardado()
let actual: Tema = elegido ?? (consulta.matches ? 'claro' : 'oscuro')
const oyentes = new Set<() => void>()

function aplicar(tema: Tema): void {
  actual = tema
  document.documentElement.dataset.tema = tema
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', COLOR_BARRA[tema])
  for (const oyente of oyentes) oyente()
}

/* Mientras el jugador no eligió, el tema sigue al sistema también en vivo: el
   teléfono que pasa a modo noche al caer el sol se lleva la página con él. */
consulta.addEventListener('change', () => {
  if (elegido === null) aplicar(consulta.matches ? 'claro' : 'oscuro')
})

aplicar(actual)

function suscribir(oyente: () => void): () => void {
  oyentes.add(oyente)
  return () => oyentes.delete(oyente)
}

/** Pasa al otro tema y lo deja elegido desde ahora. */
export function alternarTema(): void {
  elegido = actual === 'claro' ? 'oscuro' : 'claro'
  guardar(elegido)
  aplicar(elegido)
}

export function useTema(): Tema {
  return useSyncExternalStore(suscribir, () => actual)
}
